/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { keyframes } from '@emotion/react'
import { DownOutlined, UpOutlined } from '@ant-design/icons'
import { useState } from 'react'
import colors from '@sportninja/common/constants/appColors'
import type { SectionState } from '@sportninja/common/features/profile/stats/hooks/usePlayerStatsTab'
import type { RecentGameCardModel } from '@sportninja/common/features/profile/stats/types'
import { splitRecentGames } from '@sportninja/common/features/profile/stats/utils/recentGamesSplit'
import { capitalizeWords } from '@sportninja/common/ts-utils'

import Button from 'src/components/ts-components/Button'
import Text from 'src/components/ts-components/Text'

import RecentGameCard from './RecentGameCard'
import RecentGamesSkeleton from './skeletons/RecentGamesSkeleton'

interface RecentGamesProps {
  recent: SectionState<RecentGameCardModel[]>
  /** Abbr → full stat name, shown on hover of each stat label. */
  statNames: Record<string, string>
}

/**
 * The player's recent games as cards; hidden when there are none. The first
 * ten show; any more slide open below a "View More" toggle.
 */
const RecentGames = ({ recent, statNames }: RecentGamesProps) => {
  'use memo'

  const [isExpanded, setIsExpanded] = useState(false)
  const { data, isLoading, error } = recent

  if (!data && !isLoading && !error) {
    return null
  }
  if (data && data.length === 0) {
    return null
  }

  const renderBody = () => {
    if (isLoading && !data) {
      return <RecentGamesSkeleton />
    }
    if (!data) {
      return (
        <Text variant='paragraphSmall' color={colors.ERROR_200}>
          Could not load recent games.
        </Text>
      )
    }
    const { visible, more } = splitRecentGames(data)
    return (
      <>
        {/* One grid, so opened games fill the empty spots after the tenth. */}
        <div css={gridStyles}>
          {visible.map((game) => (
            <RecentGameCard
              key={game.gameId}
              game={game}
              statNames={statNames}
            />
          ))}
          {isExpanded
            ? more.map((game, index) => (
                <div key={game.gameId} css={revealStyles(index)}>
                  <RecentGameCard game={game} statNames={statNames} />
                </div>
              ))
            : null}
        </div>
        {more.length > 0 ? (
          <div css={toggleRowStyles}>
            <Button
              size='compact'
              variant='outlined'
              text={capitalizeWords(isExpanded ? 'View Less' : 'View More')}
              icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
              onClick={() => setIsExpanded((open) => !open)}
            />
          </div>
        ) : null}
      </>
    )
  }

  return (
    <section css={sectionStyles}>
      <Text variant='heading5' weight='semiBold' color={colors.WHITE}>
        {capitalizeWords('Recent Games')}
      </Text>
      {renderBody()}
    </section>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const sectionStyles = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

/**
 * `auto-fill` keeps the empty columns, so two or three games sit side by side
 * at a normal card width instead of spreading across the row. The 224px
 * minimum makes the full-width (1280px) column exactly five tracks, so five
 * games still fill it.
 */
const gridStyles = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(224px, 1fr));
  gap: 12px;
`

const revealIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

/** Delay between each opened card, so they cascade in. */
const REVEAL_STAGGER_MS = 40

/**
 * A game opened by "View More": fades and slides in, a little after the one
 * before. Stretches its card to the row height like the cards above.
 */
const revealStyles = (index: number) => css`
  display: grid;
  animation: ${revealIn} 0.25s ease both;
  animation-delay: ${index * REVEAL_STAGGER_MS}ms;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const toggleRowStyles = css`
  display: flex;
  justify-content: center;
`

export default RecentGames
